import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Grid, Row, Col, Input, Select, Modal, Switch, Button, Form, notification, Radio, Space, DatePicker} from 'antd';
import uniqid from 'uniqid';
import qs from "qs";
import useApi from '../../../hooks/useApi';
import useAccountSettings from '../../../hooks/useAccountSettings';
import useCurrency from '../../../hooks/useCurrency';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { getInvoice, updateInvoice, getEventClients } from '../../../api';
import LoadingSpinner from '../../../components/loading';
import StatusTag from '../../../components/statusTag'
import FloatingContainer from '../../../components/floatingContainer'
import InvoicePaymentTimePicker from '../../../components/invoicePaymentTimePicker'
import InvoicePaymentReminderModal from '../../../modals/invoicePaymentReminderModal';
import { HiOutlineArrowLeft } from "react-icons/hi";
import { MdOutlineClose, MdOutlineEdit } from "react-icons/md";
import { UserOutlined } from '@ant-design/icons';
import { getNumericCurrency, formatCurrencyString } from '../../../helpers/contractHelper';
import { formatDateShort, getDatePickerFormat } from '../../../helpers/dateHelper';
import InvoiceStatus from '../../../constants/invoiceStatus';
import TimePeriods from '../../../constants/timePeriods';
import TimeUnits from '../../../constants/timeUnits';
import { isEmpty, cloneDeep, isArray, toLower, isNull, isNumber } from "lodash";
import moment from "moment";
import { FaCheckCircle } from "react-icons/fa";
import { 
  renderInputField,
  renderEmailField,
  renderSearchSelectField,
} from '../../../components/formFields'
import { 
  getPaymentScheduleDueDescription, 
  getPaymentScheduleDueOption,
  getSortedPaymentReminders,
  getSortedPaymentSchedules,
  getDateFromDueDateCalculation,
  getScheduledPaymentAmount,
  getTotalScheduledPaymentAmounts
} from '../../../helpers/invoiceHelper';
import { 
  renderCurrencyField,
  renderFormLabel,
  renderPercentageField
} from '../../../components/formFields'

const EventInvoicePage = () => {

  const [isLoading, setLoading] = useState(true);
  const [eventId, setEventId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState(moment());
  const [eventInvoice, setEventInvoice] = useState({});
  const [clients, setClients] = useState([]);
  const [tempSelectedBillTo, setTempSelectedBillTo] = useState("");
  const [selectedBillTo, setSelectedBillTo] = useState("");
  const [selectedBillToFirstName, setSelectedBillToFirstName] = useState("");
  const [selectedBillToLastName, setSelectedBillToLastName] = useState("");
  const [selectedBillToEmail, setSelectedBillToEmail] = useState("");
  const [isPayNowErrorVisible, setPayNowErrorVisible] = useState(false);
  const [isPaymentScheduleActive, setPaymentScheduleActive] = useState(true);
  const [isPaymentRemindersActive, setPaymentRemindersActive] = useState(false);
  const [isTipsActive, setTipsActive] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [paymentSchedulePayments, setPaymentSchedulePayments] = useState([]);
  const [availableLineItems, setAvailableLineItems] = useState([]);
  const [isAddItemModalVisible, setAddItemModalVisible] = useState(false);
  const [isBillToModalVisible, setBillToModalVisible] = useState(false);
  const [isDateValidModalVisible, setDateValidModalVisible] = useState(false);
  const [isAmountsValidModalVisible, setAmountsValidModalVisible] = useState(false);
  const [isZeroAmountModalVisible, setZeroAmountModalVisible] = useState(false);
  const [dueDateError, setDueDateError] = useState("");
  const [fromPage, setFromPage] = useState("");
  const [hasPayments, setHasPayments] = useState(false);
  const [issueDate, setIssueDate] = useState(moment());

  // Payment Reminders
  const [isPaymentReminderModalVisible, setPaymentReminderModalVisible] = useState(false);
  const [isPaymentReminderAdd, setPaymentReminderAdd] = useState(true);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [selectedReminder, setSelectedReminder] = useState({});
  const [reminderError, setReminderError] = useState("");

  // Payment Schedule
  const [isPaymentScheduleAdd, setPaymentScheduleAdd] = useState(true);
  const [paymentScheduleError, setPaymentScheduleError] = useState("");
  const [selectedPaymentDueOption, setSelectedPaymentDueOption] = useState("ISSUE_DATE");
  const [selectedPaymentDueDateCalculation, setSelectedPaymentDueDateCalculation] = useState({});
  const [selectedDueDateOption, setSelectedDueDateOption] = useState("ISSUE_DATE");
  const [selectedDueDateCalculation, setSelectedDueDateCalculation] = useState({});
  const [selectedPaymentSchedule, setSelectedPaymentSchedule] = useState({});
  const [isPaymentScheduleModalVisible, setPaymentScheduleModalVisible] = useState(false);
  const [isFinalPaymentScheduleModalVisible, setFinalPaymentScheduleModalVisible] = useState(false);

  useDocumentTitle("Edit Invoice")
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [sendRequest] = useApi()
  const [accountSettings] = useAccountSettings()
  const [currency] = useCurrency()
  const [billToForm] = Form.useForm();
  const [paymentScheduleForm] = Form.useForm();

  const invoiceId = params.id;

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshPage()
    const queryStrings = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (queryStrings.from) {
      setFromPage(queryStrings.from)
    }
  }, []);

  const refreshPage = async () => {
    try {
      const results = await sendRequest(getInvoice(invoiceId))

      // Navigate to event page if the invoice is paid (cannot edit paid invoices)
      if (results.status == InvoiceStatus.PAID) {
        navigate(`/admin/events/${results.event_id}?tab=documents`)
        return
      }

      setEventInvoice(results)
      setEventId(results.event_id)
      setInvoiceNumber(results.invoice_number)
      setSelectedBillTo(results.bill_to_user_id ? results.bill_to_user_id : 1)
      if (!isEmpty(results.bill_to_user)) {
        setSelectedBillToFirstName(results.bill_to_user.first_name)
        setSelectedBillToLastName(results.bill_to_user.last_name)
        setSelectedBillToEmail(results.bill_to_user.email)
      }
      setDueDate(moment.utc(results.due_date))
      setIssueDate(moment(results.issue_date, "YYYY-MM-DD"))
      setLineItems(results.line_items ?? [])
      setAvailableLineItems(results.available_line_items ?? [])
      setNotes(results.notes)

      const scheduledPayments = results.scheduled_payments ?? []
      setPaymentSchedulePayments(scheduledPayments)
      setPaymentScheduleActive(results.has_payment_schedule == true)
      setTipsActive(results.tips ?? false)

      // Reminders
      const reminders = isArray(results.payment_reminders) ? results.payment_reminders : []
      if (reminders.length > 0) {
        setPaymentRemindersActive(true)
        setPaymentReminders(reminders)
      }

      const hasPaymentsResults = isArray(results.payments) && results.payments.length > 0;
      setHasPayments(hasPaymentsResults)

      const clientResults = await sendRequest(getEventClients(results.event_id))
      setClients(clientResults)
    } finally {
      setLoading(false)
    }
  }

  const getIssueDate = () => {
    return issueDate.format("YYYY-MM-DD")
  }

  const confirmIssueDate = (date) => {
    setIssueDate(date)
    const clonedPayments = cloneDeep(paymentSchedulePayments)
    const newPayments = clonedPayments.map(x => {
      return {
        ...x,
        due_date: getDateFromDueDateCalculation(x.due_date_calculation, eventInvoice.event_date, date.format("YYYY-MM-DD"))
      }
    })
    setPaymentSchedulePayments(newPayments)
  }

  const onNext = async () => {

    if (hasPayments) {
      navigateNext()
      return
    }

    // Check if due dates are valid
    if (isPaymentScheduleActive) {
      var isDatesValid = true

      // Check if all dates are in the future
      for (const payment of paymentSchedulePayments) {
        const paymentDueDate = moment(payment.due_date, "YYYY-MM-DD")
        const isBeforeToday = paymentDueDate.isBefore(moment().startOf("day"))
        if (isBeforeToday) {
          isDatesValid = false
        }
      }

      if (!isDatesValid) {
        setDueDateError("All due dates in the payment schedule must be in the future. Please adjust the due dates of your payments before continuing.")
        setDateValidModalVisible(true)
        return
      }

      // Check if all non-final payments are before the final payment due date
      const nonFinalPayments = paymentSchedulePayments.filter(x => !x.final_payment)
      const finalPayments = paymentSchedulePayments.filter(x => x.final_payment)
      const finalDate = finalPayments.length > 0 ? finalPayments[0].due_date : moment().format("YYYY-MM-DD")
      for (const payment of nonFinalPayments) {
        const paymentDueDate = moment(payment.due_date, "YYYY-MM-DD")
        const finalDueDate = moment(finalDate, "YYYY-MM-DD")
        const isAfterFinal = paymentDueDate.isAfter(finalDueDate)
        if (isAfterFinal) {
          isDatesValid = false
        }
      }
  
      if (!isDatesValid) {
        setDueDateError("All due dates in the payment schedule must be before the due date of the final payment. Please adjust the due dates of your payments before continuing.")
        setDateValidModalVisible(true)
        return
      }
    }

    // Make sure the total payments is not more than the amount due
    let totalAmountDue = getAmountDue(lineItems)
    let totalPayments = getTotalScheduledPaymentAmounts(paymentSchedulePayments, totalAmountDue)
    if (totalPayments > totalAmountDue) {
      setAmountsValidModalVisible(true)
      return
    }

    // Make sure any of the non-final payments are not zero
    var isAnyAmountZero = false
    const nonFinalPayments = paymentSchedulePayments.filter(x => !x.final_payment)
    for (const payment of nonFinalPayments) {
      const paymentAmount = getScheduledPaymentAmount(payment, totalAmountDue)
      if (paymentAmount == 0) {
        isAnyAmountZero = true
      }
    }

    // Make sure the final payment is not zero
    if (totalPayments == totalAmountDue) {
      isAnyAmountZero = true
    }

    if (isAnyAmountZero) {
      setZeroAmountModalVisible(true)
      return
    }

    try {
      const isBillToCustom = selectedBillTo == 1
      const body = {
        status: InvoiceStatus.DRAFT,
        invoice_number: invoiceNumber,
        bill_to_user_id: isBillToCustom ? null : selectedBillTo,
        bill_to_first_name: isBillToCustom ? selectedBillToFirstName : null,
        bill_to_last_name: isBillToCustom ? selectedBillToLastName : null,
        bill_to_email: isBillToCustom ? selectedBillToEmail : null,
        line_items: lineItems,
        total: getAmountDue(lineItems),
        has_payment_schedule: isPaymentScheduleActive,
        scheduled_payments: paymentSchedulePayments,
        notes: notes,
        tips: isTipsActive,
        payment_reminders: isPaymentRemindersActive ? paymentReminders : [],
        issue_date: getIssueDate()
      }
      await sendRequest(updateInvoice(invoiceId, body))
      navigateNext()
    } catch {
      notification.error({
        message: 'Error',
        description: 'Something went wrong.',
        duration: 3
      });
    }
  }

  const navigateNext = () => {
    if (fromPage == "invoices") {
      navigate(`/admin/invoices/${invoiceId}/preview?from=invoices-edit`)
    } else if (fromPage == "invoice-details") {
      navigate(`/admin/invoices/${invoiceId}/preview?from=invoice-details-edit`)
    } else {
      navigate(`/admin/invoices/${invoiceId}/preview?from=edit`)
    }
  }

  const onDueDateOptionChange = (value) => {
    setSelectedDueDateOption(value)
    if (value == "EVENT_DATE") {
      setSelectedDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE_EVENT_DATE })
    } else if (value == "ISSUE_DATE") {
      setSelectedDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER_ISSUE_DATE })
    }
  }

  const onPaymentDueOptionChange = (value) => {
    setSelectedPaymentDueOption(value)
    if (value == "EVENT_DATE") {
      setSelectedPaymentDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE_EVENT_DATE })
    } else if (value == "ISSUE_DATE") {
      setSelectedPaymentDueDateCalculation({ time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER_ISSUE_DATE })
    }
  }

  const addDefaultPaymentSchedule = () => {
    const dueDateCalculation = {
      time_value: 0, 
      time_unit: TimeUnits.DAYS,
      time_period: TimePeriods.AFTER_ISSUE_DATE
    }
    const defaultPayment = {
      scheduled_payment_id: uniqid(),
      scheduled_amount_type: "PERCENTAGE",
      scheduled_amount: 50,
      due_date_calculation: dueDateCalculation,
      due_date: getDateFromDueDateCalculation(dueDateCalculation, eventInvoice.event_date, getIssueDate()), 
      final_payment: false,
      status: "UNPAID",
      payment_amount: 0
    }
    var clonedPayments = cloneDeep(paymentSchedulePayments)
    clonedPayments.push(defaultPayment)
    setPaymentSchedulePayments(clonedPayments)
  }

  const onAddPaymentSchedule = () => {
    setPaymentScheduleAdd(true)
    setPaymentScheduleError("")
    setSelectedPaymentDueOption("CUSTOM")
    const dueDateCalculation = {
      time_value: 0, 
      time_unit: TimeUnits.DAYS,
      time_period: TimePeriods.AFTER_ISSUE_DATE
    }
    const defaultPayment = {
      scheduled_payment_id: uniqid(),
      scheduled_amount_type: "AMOUNT",
      scheduled_amount: null,
      due_date_calculation: dueDateCalculation,
      due_date: getDateFromDueDateCalculation(dueDateCalculation, eventInvoice.event_date, getIssueDate()),
      final_payment: false,
      status: "UNPAID",
      payment_amount: "0"
    }
    setSelectedPaymentSchedule(defaultPayment)
    setSelectedPaymentDueDateCalculation(dueDateCalculation)
    const data = {
      amount: "",
      percentage: ""
    }
    paymentScheduleForm.setFieldsValue(data)
    setPaymentScheduleModalVisible(true)
  }

  const getDiscountText = (item) => {
    if (item.discount_type == "PERCENTAGE") {
      return `Discount applied (${item.discount_percentage})`
    } else {
      return "Discount applied"
    }
  }

  const getAmountDue = (items) => {
    return items.reduce((x,y) => {
      return x + Number(y.grand_total);
    }, 0)
  }

  const getSubTotal = (items) => {
    return items.reduce((x,y) => {
      return x + Number(y.total_subtotal);
    }, 0)
  }

  const getTotalTaxes = (items) => {
    return items.reduce((x,y) => {
      return x + Number(y.total_tax);
    }, 0)
  }

  const addLineItem = (item) => {
    var newLineItems = cloneDeep(lineItems)
    newLineItems.push(item)
    setLineItems(newLineItems)
  }

  const removeLineItem = (id) => {
    var newLineItems = cloneDeep(lineItems)
    newLineItems = newLineItems.filter(x => x.id != id)
    setLineItems(newLineItems)
  }

  const onEditBillToUser = () => {
    setTempSelectedBillTo(selectedBillTo)
    billToForm.resetFields()
    billToForm.setFieldsValue({ bill_to: selectedBillTo })
    if (selectedBillTo == 1) {
      billToForm.setFieldsValue({ first_name: selectedBillToFirstName, last_name: selectedBillToLastName, email: selectedBillToEmail })
    }
    setBillToModalVisible(true)
  }

  const saveBillToUser = (values) => {
    setSelectedBillTo(values["bill_to"])
    if (values["bill_to"] == 1) {
      setSelectedBillToFirstName(values["first_name"])
      setSelectedBillToLastName(values["last_name"])
      setSelectedBillToEmail(values["email"])
    } else {
      const clientResult = clients.find(x => x.user_id == values["bill_to"])
      if (!isEmpty(clientResult)) {
        setSelectedBillToFirstName(clientResult.first_name)
        setSelectedBillToLastName(clientResult.last_name)
        setSelectedBillToEmail(clientResult.email)
      }
    }
    setBillToModalVisible(false)
  }

  const onEditPaymentSchedule = (payment) => {
    setPaymentScheduleAdd(false)
    setPaymentScheduleError("")
    setSelectedPaymentSchedule(payment)

    if (payment.final_payment) {
      setSelectedDueDateOption(getPaymentScheduleDueOption(selectedDueDateCalculation))
      setSelectedDueDateCalculation(payment.due_date_calculation)
      setFinalPaymentScheduleModalVisible(true)
    } else {
      const data = {
        amount: payment.scheduled_amount_type == "AMOUNT" ? payment.scheduled_amount : "",
        percentage: payment.scheduled_amount_type == "PERCENTAGE" ? payment.scheduled_amount : "",
      }
      paymentScheduleForm.setFieldsValue(data)
      setSelectedPaymentDueOption(getPaymentScheduleDueOption(payment))
      setSelectedPaymentDueDateCalculation(payment.due_date_calculation)
      setPaymentScheduleModalVisible(true)
    }
  }

  const onRemovePaymentSchedule = (payment) => {
    var clonedPayments = cloneDeep(paymentSchedulePayments)
    clonedPayments = clonedPayments.filter(x => x.scheduled_payment_id != payment.scheduled_payment_id)
    setPaymentSchedulePayments(clonedPayments)
  }

  const onPaymentScheduleToggle = (value) => {
    setPaymentScheduleActive(value)
    if (value && paymentSchedulePayments.length == 1) {
      addDefaultPaymentSchedule()
    } else {
      var clonedPayments = cloneDeep(paymentSchedulePayments)
      clonedPayments = clonedPayments.filter(x => x.final_payment)
      setPaymentSchedulePayments(clonedPayments)
    }
  }

  const onPaymentReminderToggle = (value) => {
    setPaymentRemindersActive(value)
    if (paymentReminders.length == 0) {
      const defaultReminders = [
        { id: uniqid(), time_value: 7, time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE },
        { id: uniqid(), time_value: 0, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER },
        { id: uniqid(), time_value: 7, time_unit: TimeUnits.DAYS, time_period: TimePeriods.AFTER }
      ]
      setPaymentReminders(defaultReminders)
    }
  }

  const onAddReminder = () => {
    const defaultPaymentReminder = { id: uniqid(), time_value: "", time_unit: TimeUnits.DAYS, time_period: TimePeriods.BEFORE }
    setSelectedReminder(defaultPaymentReminder)
    setPaymentReminderAdd(true)
    setReminderError("")
    setPaymentReminderModalVisible(true)
  }

  const onEditReminder = (reminder) => {
    setSelectedReminder(reminder)
    setPaymentReminderAdd(false)
    setReminderError("")
    setPaymentReminderModalVisible(true)
  }

  const onRemoveReminder = (id) => {
    var clonedReminders = cloneDeep(paymentReminders)
    clonedReminders = clonedReminders.filter(x => x.id != id)
    setPaymentReminders(clonedReminders)
  }

  const submitPaymentReminder = () => {
    var clonedReminders = cloneDeep(paymentReminders)
    
    // Validate the form
    if (isNull(selectedReminder.time_value) || selectedReminder.time_value == "") {
      setReminderError("Please enter a number before continuing.")
      return
    }

    // Check if reminder has already been added
    const foundSameReminder = clonedReminders.find(x => {
      return x.time_value == selectedReminder.time_value && x.time_unit == selectedReminder.time_unit && x.time_period == selectedReminder.time_period && x.id != selectedReminder.id
    })
    if (!isEmpty(foundSameReminder)) {
      setReminderError("A reminder already exists on this day. Please try again.")
      return
    }

    // If it's an edit, remove the reminder entirely before adding it back
    if (!isPaymentReminderAdd) {
      clonedReminders = clonedReminders.filter(x => x.id != selectedReminder.id)
    }

    const newReminder = {
      ...selectedReminder,
      time_value: Number(selectedReminder.time_value)
    }

    clonedReminders.push(newReminder)
    setPaymentReminders(clonedReminders)
    setPaymentReminderModalVisible(false)
  }

  const submitPaymentSchedule = (values) => {
    var clonedPayments = cloneDeep(paymentSchedulePayments)

    // Validate the form
    if (selectedPaymentDueOption == "CUSTOM" && !isNumber(selectedPaymentDueDateCalculation.time_value) && selectedPaymentDueDateCalculation.time_value == "") {
      setPaymentScheduleError("Please enter a number before continuing.")
      return
    }

    // Check if payment has already been added
    const foundSameItem = clonedPayments.find(x => {
      const calculation = x.due_date_calculation
      return calculation.time_value == Number(selectedPaymentDueDateCalculation.time_value) && calculation.time_unit == selectedPaymentDueDateCalculation.time_unit && calculation.time_period == selectedPaymentDueDateCalculation.time_period && x.scheduled_payment_id != selectedPaymentSchedule.scheduled_payment_id
    })
    if (!isEmpty(foundSameItem)) {
      setPaymentScheduleError("A payment already exists on this day. Please select another day.")
      return
    }

    // Create new payment object for saving
    const scheduledAmount = selectedPaymentSchedule.scheduled_amount_type == "AMOUNT" ? getNumericCurrency(values["amount"]) : values["percentage"]
    const newPayment = {
      scheduled_payment_id: selectedPaymentSchedule.scheduled_payment_id,
      due_date_calculation: selectedPaymentDueDateCalculation,
      due_date: getDateFromDueDateCalculation(selectedPaymentDueDateCalculation, eventInvoice.event_date, getIssueDate()),
      scheduled_amount: scheduledAmount,
      scheduled_amount_type: selectedPaymentSchedule.scheduled_amount_type,
      final_payment: false,
      status: selectedPaymentSchedule.status
    }

    // If it's an edit, remove the payment entirely before adding it back
    if (!isPaymentScheduleAdd) {
      clonedPayments = clonedPayments.filter(x => x.scheduled_payment_id != newPayment.scheduled_payment_id)
    }

    clonedPayments.push(newPayment)
    setPaymentSchedulePayments(clonedPayments)
    setPaymentScheduleModalVisible(false)
  }

  const submitFinalPaymentSchedule = () => {
    var clonedPayments = cloneDeep(paymentSchedulePayments)

    const newPayment = {
      scheduled_payment_id: selectedPaymentSchedule.scheduled_payment_id,
      due_date_calculation: selectedDueDateCalculation,
      due_date: getDateFromDueDateCalculation(selectedDueDateCalculation, eventInvoice.event_date, getIssueDate()),
      scheduled_amount: 0,
      scheduled_amount_type: selectedPaymentSchedule.scheduled_amount_type,
      final_payment: true,
      status: selectedPaymentSchedule.status
    }

    // Remove the existing payment before replacing it
    clonedPayments = clonedPayments.filter(x => x.scheduled_payment_id != newPayment.scheduled_payment_id)

    clonedPayments.push(newPayment)
    setPaymentSchedulePayments(clonedPayments)
    setFinalPaymentScheduleModalVisible(false)
  }

  const renderHeaderButton = () => {
    const buttonText = hasPayments ? "Next: Preview" : "Next: Save & Preview"
    return <button className="small-primary-button" style={{ }} onClick={() => onNext()}>{buttonText}</button>;
  }

  const renderHeader = () => {
    var backPath = `/admin/events/${eventId}?tab=documents`
    if (fromPage == "invoices") {
      backPath = `/admin/invoices`
    } else if (fromPage == "invoice-details") {
      backPath = `/admin/invoices/${invoiceId}`
    }

    if (screens.md) {
      return (
        <div className="event-contract--header p-20"> 
          <div className="flex-row flex-middle">
            <div className="fs-20 display-flex mr-10" onClick={() => navigate(backPath)}><HiOutlineArrowLeft/></div>
            <div className="fs-18 fw-700 mr-15 flex-ellipsis">
              Invoice #{ eventInvoice.invoice_number }
            </div>
            <StatusTag status={eventInvoice.status}/>
            <div className="flex-1"></div>
            <div className="">
              { renderHeaderButton()}
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className="event-contract--header p-20">
          <div className="flex-row flex-middle">
            <div className="fs-20 display-flex mr-10" onClick={() => navigate(backPath)}><HiOutlineArrowLeft/></div>
            <div className="fs-18 fw-700 mr-15 flex-ellipsis">
              Invoice #{ eventInvoice.invoice_number }
            </div>
            <StatusTag status={eventInvoice.status}/>
            <div className="flex-1"></div>
          </div>
          <div className="text-center mt-15">
            <div className="">
              { renderHeaderButton()}
            </div>
          </div>
        </div>
      )
    }
  }

  const renderPayNowErrorModal = () => {
    return (
      <Modal visible={isPayNowErrorVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setPayNowErrorVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Payment Link Needed</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">In order to include a <b>Pay Invoice</b> button, you will need to configure a Payment Link by going to the <b>Invoices</b> page under Account Setup.</div>
          <button className="primary-button" type="button" onClick={() => setPayNowErrorVisible(false)}>Got it!</button>
        </div>
      </Modal>
    )
  }

  const renderBillToUser = () => {
    var clientPhoto = null;
    return (
      <Row gutter={[15,15]} align="middle" className="">
        <Col flex={0}>
          {/* { clientPhoto ? (
            <div className="small-profile-photo size-60" style={{ backgroundImage: `url(${clientPhoto})`}} onClick={() => displayPhoto(clientPhoto)}></div>
          ) : ( */}
            <div className="display-flex">
              <div className="user-profile-circle small"><UserOutlined/></div>
            </div>
          {/* )} */}
        </Col>
        <Col flex={1}>
          <div className="fs-14 fw-700">{selectedBillToFirstName} {selectedBillToLastName}</div>
          <div className="fs-14 fw-500 c-text-gray line-1-1">{selectedBillToEmail}</div>
        </Col>
        <Col flex={0}>
          <div className="blue-link" onClick={() => onEditBillToUser()}>Edit</div>
        </Col>
      </Row>
    )
  }

  const renderPaymentScheduleActions = (payment) => {
    return (
      <div className="display-flex" style={{ justifyContent: 'flex-end'}}>
        <div className="display-flex ph-5" onClick={() => onEditPaymentSchedule(payment)}>
          <MdOutlineEdit size={20} color={"#CCC"}/>
        </div>
        { paymentSchedulePayments.length > 2 && payment.final_payment != true && (
          <div className="display-flex ph-5" onClick={() => onRemovePaymentSchedule(payment)}>
            <MdOutlineClose size={24} color={"#CCC"}/>
          </div>
        )}
      </div>
    )
  }

  const renderPaymentScheduleRow = (item, index) => {
    const dueDateCalculation = item.due_date_calculation
    const paymentDueDate = moment(item.due_date, "YYYY-MM-DD")
    const dueDescription = getPaymentScheduleDueDescription(dueDateCalculation)
    const formattedPaymentDueDate = formatDateShort(item.due_date, accountSettings)

    const isDateInvalid = paymentDueDate.isBefore(moment().startOf("day"))
    const dateClass = isDateInvalid ? "c-red" : "c-text-gray"

    // Calculate the payment amount
    let amountDue = getAmountDue(lineItems)
    var paymentAmount = 0
    if (item.final_payment) {
      let totalPayments = getTotalScheduledPaymentAmounts(paymentSchedulePayments, amountDue)
      paymentAmount = amountDue - totalPayments
    } else {
      paymentAmount = getScheduledPaymentAmount(item, amountDue)
    }
    const amountClass = paymentAmount <= 0 ? "c-red" : ""

    return (
      <>
        <Row gutter={[15,5]} className="bg-gray ph-10 pv-15 mt-10">
          <Col xs={12} md={4} className="c-text-gray">
            { index + 1 } of { paymentSchedulePayments.length }
            { item.final_payment && (
              <div className="c-text-gray fs-10">(Final payment)</div>
            )}
          </Col>
          { !screens.md && (
            <Col xs={12}>
              { renderPaymentScheduleActions(item) }
            </Col>
          )}
          <Col xs={24} md={6} className="pl-10">
            <span className={amountClass}>{ formatCurrencyString(paymentAmount, currency.code) }</span>
            { item.scheduled_amount_type == "PERCENTAGE" && (
              <span className={`ml-5 c-text-gray`}>({item.scheduled_amount}%)</span>
            )}
            { item.final_payment && (
              <div className="c-text-gray fs-10">(Automatically calculated)</div>
            )}
          </Col>
          <Col xs={24} md={10} className="pl-10">
            { dueDescription }
            <div className={`${dateClass} fs-12`}>{ formattedPaymentDueDate }</div>
          </Col>
          { screens.md && (
            <Col xs={4}>
              { renderPaymentScheduleActions(item) }
            </Col>
          )}
        </Row>
      </>
    )
  }

  const renderPaymentSchedule = () => {
    return (
      <>
        { screens.md ? (
          <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
            <Col xs={4}>
              PAYMENT
            </Col>
            <Col xs={6} className="pl-10">
              AMOUNT
            </Col>
            <Col xs={10} className="pl-10">
              DUE
            </Col>
          </Row>
        ) : (
          <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
            <Col xs={24}>
              PAYMENT / AMOUNT / DUE
            </Col>
          </Row>
        )}
        { getSortedPaymentSchedules(paymentSchedulePayments).map((x,i) => renderPaymentScheduleRow(x,i))}
        { paymentSchedulePayments.length < 6 && (
          <div className="blue-link mt-15" onClick={() => onAddPaymentSchedule()}>+ Add Payment</div>
        )}
      </>
    )
  }

  const renderSummaryHeader = () => {
    if (screens.md) {
      return (
        <Row align="middle" className="pv-15 t-border b-border ph-15 c-text-gray bg-gray">
          <Col xs={10}>
            <div className="fs-12 fw-700">ITEM</div>
          </Col>
          <Col xs={4}>
            <div className="fs-12 fw-700 text-right">QTY</div>
          </Col>
          <Col xs={4}>
            <div className="fs-12 fw-700 text-right">PRICE</div>
          </Col>
          <Col xs={4}>
            <div className="fs-12 fw-700 text-right">AMOUNT</div>
          </Col>
        </Row> 
      )
    } else {
      return (
        <Row align="middle" className="pv-15 t-border b-border bg-gray ph-15">
          <Col xs={12}>
            <div className="fs-12 fw-700">ITEM</div>
            <div className="fs-12 fw-700">QTY / PRICE</div>
          </Col>
          <Col xs={12}>
            <div className="fs-12 fw-700 text-right">AMOUNT</div>
          </Col>
        </Row> 
      )
    }
  }

  const renderCommonLineItem = (item, index) => {
    if (screens.md) {
      return (
        <Row className="pv-30 ph-15 border mt-10" key={index}>
          <Col xs={10}>
            <div className="fw-700">{item.name}</div>
            { item.discount && (
              <div className="fs-12 c-blue ml-10">{ getDiscountText(item) }</div>
            )}
          </Col>
          <Col xs={4}>
            <div className="text-right">{item.quantity}</div>
          </Col>
          <Col xs={4}>
            <div className="text-right">{formatCurrencyString(item.item_price, currency.code)}</div>
          </Col>
          <Col xs={4}>
            <div className="fs-14 fw-500 text-right">{ formatCurrencyString(item.total_price, currency.code) }</div>
            { item.discount && (
              <div className="fs-12 c-blue text-right">- { formatCurrencyString(item.total_discount, currency.code) }</div>
            )}
          </Col>
          <Col xs={2} style={{ alignSelf: 'center'}}>
            <div className="display-flex" style={{ justifyContent: 'flex-end'}} onClick={() => removeLineItem(item.id)}>
              <MdOutlineClose size={20} color={"#CCC"}/>
            </div>
          </Col>
        </Row> 
      )
    } else {
      return (
        <Row className="pv-20 ph-15 border mt-10" key={index}>
          <Col xs={15}>
            <div className="fw-700">{item.name}</div>
            <div className="">{item.quantity} x {formatCurrencyString(item.item_price, currency.code)}</div>
            { item.discount && (
              <div className="fs-12 c-blue ml-10">{ getDiscountText(item) }</div>
            )}
          </Col>
          <Col xs={6}>
            <div className="fs-14 fw-500 text-right">{ formatCurrencyString(item.total_price, currency.code) }</div>
            { item.discount && (
              <div className="fs-12 c-blue text-right">- { formatCurrencyString(item.total_discount, currency.code) }</div>
            )}
          </Col>
          <Col xs={3} style={{ alignSelf: 'center'}}>
            <div className="display-flex" style={{ justifyContent: 'flex-end'}} onClick={() => removeLineItem(item.id)}>
              <MdOutlineClose size={20} color={"#CCC"}/>
            </div>
          </Col>
        </Row> 
      )
    }
  }

  const renderAvailableLineItem = (item, index) => {
    const lineItemIds = lineItems.map(x => x.id)
    const selected = lineItemIds.includes(item.id)
    return (
      <div className="border p-15 mb-10" key={index}>
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-600">{item.name}</div>
            <div>{item.quantity} x {formatCurrencyString(item.item_price, currency.code)}</div>
          </Col>
          <Col>
            { selected ? (
              <div className="display-flex"><FaCheckCircle style={{ fontSize: 20, color: '#536DFE' }}/></div>
            ) : (
              <div className="blue-link" onClick={() => addLineItem(item)}>Add</div>
            )}
          </Col>
        </Row>
      </div>
    )
  }

  const renderAvailableLineItems = () => {
    const availablePackages = availableLineItems.filter(x => x.type == "PACKAGE")
    const availableAddOns = availableLineItems.filter(x => x.type == "ADD_ON")

    if (lineItems.length == 0 && availableLineItems.length == 0) {
      return (
        <div className="bg-gray ph-15 pv-30 text-center">There are no packages or add-ons for this event. Navigate to the event details page to add services.</div>
      )
    }

    return (
      <div>
        { availablePackages.length > 0 && (
          <>
            <div className="fw-600 fs-12 mb-5 ml-5 mt-10 c-text-gray">PACKAGES</div>
            { availablePackages.map((x,i) => renderAvailableLineItem(x,i))}
          </>
        )}
        { availableAddOns.length > 0 && (
          <>
            <div className="fw-600 fs-12 mb-5 ml-5 mt-10 c-text-gray">ADD-ONS</div>
            { availableAddOns.map((x,i) => renderAvailableLineItem(x,i))}
          </>
        )}
      </div>
    )
  }

  const renderAddItemModal = () => {
    return (
      <Modal visible={isAddItemModalVisible} footer={null} onCancel={() => setAddItemModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-700 fs-18">Add Item</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => setAddItemModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <div className="">
          <div className="pt-10 pb-15">
            { renderAvailableLineItems() }
          </div>
          <div className="text-right">
            <Button className="admin-small-button" onClick={() => setAddItemModalVisible(false)}>Done</Button>
          </div>
        </div>
      </Modal>
    )
  }

  const renderBillToModal = () => {
    var clientData = clients.map((client) => {
      return {
        value: client.user_id,
        text: client.first_name + " " + client.last_name
      }
    })
    clientData.push({ value: 1, text: "Other" })
    return (
      <Modal visible={isBillToModalVisible} footer={null} onCancel={() => setBillToModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-700 fs-18">Edit Bill To</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => setBillToModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <div className="">
          <Form form={billToForm} layout="vertical" name="client" onFinish={saveBillToUser}>
            <div className="">
              <div className="pt-10 pb-15">
                <Row align="middle" gutter={[15,15]}>
                  <Col xs={24}>
                    { renderSearchSelectField("Bill To", "bill_to", "Select a person...", clientData, true, (value) => setTempSelectedBillTo(value))}
                  </Col>
                  { tempSelectedBillTo == 1 && (
                    <>
                      <Col xs={24} sm={12}>
                        { renderInputField("First Name", "first_name", true)}
                      </Col>
                      <Col xs={24} sm={12}>
                        { renderInputField("Last Name", "last_name", true)}
                      </Col>
                      <Col xs={24}>
                        { renderEmailField("Email", "email", true)}
                      </Col>
                    </>
                  )}
                </Row>
              </div>
              <div className="text-right">
                <Button className="admin-small-button secondary mr-10" onClick={() => setBillToModalVisible(false)}>Cancel</Button>
                <Button className="admin-small-button" htmlType="submit">Save</Button>
              </div>
            </div>
          </Form>
        </div>
      </Modal>
    )
  }

  const renderDateValidModal = () => {
    return (
      <Modal visible={isDateValidModalVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setDateValidModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Payment Schedule Error</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">{dueDateError}</div>
          <button className="primary-button" type="button" onClick={() => setDateValidModalVisible(false)}>OK</button>
        </div>
      </Modal>
    )
  }

  const renderAmountsValidModal = () => {
    return (
      <Modal visible={isAmountsValidModalVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setAmountsValidModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Payment Amounts</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">The total amount of payments must not be greater than the amount due. Please adjust your payment schedule before continuing.</div>
          <button className="primary-button" type="button" onClick={() => setAmountsValidModalVisible(false)}>OK</button>
        </div>
      </Modal>
    )
  }

  const renderZeroAmountModal = () => {
    return (
      <Modal visible={isZeroAmountModalVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setZeroAmountModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Invalid Amounts</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">All amounts in the payment schedule must be greater than {formatCurrencyString(0, currency.code)}.</div>
          <button className="primary-button" type="button" onClick={() => setZeroAmountModalVisible(false)}>OK</button>
        </div>
      </Modal>
    )
  }

  const renderPaymentReminderActions = (reminder) => {
    return (
      <div className="display-flex" style={{ justifyContent: 'flex-end'}}>
        <div className="display-flex ph-5" onClick={() => onEditReminder(reminder)}>
          <MdOutlineEdit size={20} color={"#CCC"}/>
        </div>
        { paymentReminders.length > 1 && (
          <div className="display-flex ph-5" onClick={() => onRemoveReminder(reminder.id)}>
            <MdOutlineClose size={24} color={"#CCC"}/>
          </div>
        )}
      </div>
    )
  }

  const renderPaymentReminder = (reminder, index) => {
    var reminderDescription = `${ reminder.time_value } ${ toLower(reminder.time_unit) } ${ toLower(reminder.time_period) } due date`
    if (reminder.time_value == 0) {
      reminderDescription = "On due date"
    }

    return (
      <Row gutter={[15,5]} className="bg-gray ph-10 pv-15 mt-10">
        <Col xs={12} md={4} className="c-text-gray">
          { index + 1 } of { paymentReminders.length }
        </Col>
        { !screens.md && (
          <Col xs={12}>
            { renderPaymentReminderActions(reminder) }
          </Col>
        )}
        <Col xs={16} className="pl-10">
          {reminderDescription} 
        </Col>
        { screens.md && (
          <Col xs={4}>
            { renderPaymentReminderActions(reminder) }
          </Col>
        )}
      </Row>
    )
  }

  const renderPaymentReminders = () => {
    return (
      <div>
        <Row gutter={[15,15]} className="b-border mt-20 pb-10 c-text-gray fs-12 fw-700 pl-10">
          <Col xs={24} sm={4}>
            REMINDER
          </Col>
          { screens.md && (
            <Col xs={20} className="pl-10">
              WHEN?
            </Col>
          )}
        </Row>
        { getSortedPaymentReminders(paymentReminders).map((x,i) => renderPaymentReminder(x,i)) }
        { paymentReminders.length <= 8 && (
          <div className="blue-link mt-10" onClick={onAddReminder}>+ Add Reminder</div>
        )}
      </div>
    )
  }

  const renderPaymentReminderModal = () => {
    return (
      <InvoicePaymentReminderModal 
        isVisible={isPaymentReminderModalVisible}
        setVisible={setPaymentReminderModalVisible}
        title={isPaymentReminderAdd ? "Add Payment Reminder" : "Edit Payment Reminder" } 
        selectedReminder={selectedReminder}
        setSelectedReminder={setSelectedReminder}
        submit={submitPaymentReminder}
        reminderError={reminderError}
      />
    )
  }

  const renderPaymentScheduleModal = () => {
    return (
      <Modal visible={isPaymentScheduleModalVisible} footer={null} onCancel={() => setPaymentScheduleModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Form form={paymentScheduleForm} layout="vertical" name="edit-package" onFinish={submitPaymentSchedule}>
          <Row align="middle">
            <Col flex={1}>
              <div className="fw-700 fs-18">{isPaymentScheduleAdd ? "Add" : "Edit" } Scheduled Payment</div>
            </Col>
            <Col>
              <div className="display-flex" onClick={() => setPaymentScheduleModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
            </Col>
          </Row>
          <Row className="mt-20" gutter={[15, 15]}>
            <Col xs={24}>
              { renderFormLabel("Type")}
              <Radio.Group value={selectedPaymentSchedule.scheduled_amount_type} onChange={(e) => setSelectedPaymentSchedule({ ...selectedPaymentSchedule, scheduled_amount_type: e.target.value})}>
                <Space direction="vertical">
                  <Radio value="AMOUNT">Amount</Radio>
                  <Radio value="PERCENTAGE">Percentage</Radio>
                </Space>
              </Radio.Group>
            </Col>
            { selectedPaymentSchedule.scheduled_amount_type == "AMOUNT" && (
              <Col xs={24}>
                { renderCurrencyField("Amount", "amount", true, currency.code) }
              </Col>
            )}
            { selectedPaymentSchedule.scheduled_amount_type == "PERCENTAGE" && (
              <Col xs={24}>
                { renderPercentageField("Percentage", "percentage", true) }
              </Col>
            )}
            <Col xs={24}>
              { renderFormLabel("When?")}
              <Row>
                <Col xs={24}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={"Select an option..."}
                    size='large'
                    value={selectedPaymentDueOption}
                    onChange={(value) => onPaymentDueOptionChange(value)}
                  >
                    <Select.Option value="ISSUE_DATE">On issue date</Select.Option>
                    <Select.Option value="EVENT_DATE">On event date</Select.Option>
                    <Select.Option value="CUSTOM">Custom</Select.Option>
                  </Select>
                </Col>
              </Row>
              { selectedPaymentDueOption == "CUSTOM" &&  (
                <InvoicePaymentTimePicker timeObject={selectedPaymentDueDateCalculation} setTimeObject={setSelectedPaymentDueDateCalculation}/>
              )}
            </Col>
          </Row>
          <Row gutter={[10,10]} className="mt-20">
            <Col xs={24}>
              <div className="fs-12 c-red text-center">{ paymentScheduleError }</div>
            </Col>
            <Col xs={24}>
              <div className="mt-20 text-right">
                <Button className="admin-small-button secondary" onClick={() => setPaymentScheduleModalVisible(false)}>Cancel</Button>
                <Button className="admin-small-button ml-10" htmlType="submit">Save</Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    )
  }

  const renderFinalPaymentScheduleModal = () => {
    return (
      <Modal visible={isFinalPaymentScheduleModalVisible} footer={null} onCancel={() => setFinalPaymentScheduleModalVisible(false)} width={500} closable={false} maskClosable={false} wrapClassName="rounded-modal">
        <Row align="middle">
          <Col flex={1}>
            <div className="fw-700 fs-18">Edit Payment</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => setFinalPaymentScheduleModalVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <Row className="mt-20" gutter={[15, 15]}>
          <Col xs={24}>
            { renderFormLabel("Amount")}
            <div className="bg-gray p-10">
              Since this is the final payment, the balance will be automatically calculated.
            </div>
          </Col>
          <Col xs={24}>
            { renderFormLabel("When?")}
            { renderDueDateOption() }
          </Col>
        </Row>
        <Row gutter={[10,10]} className="mt-20">
          <Col xs={24}>
            <div className="fs-12 c-red text-center">{ paymentScheduleError }</div>
          </Col>
          <Col xs={24}>
            <div className="mt-20 text-right">
              <Button className="admin-small-button secondary" onClick={() => setFinalPaymentScheduleModalVisible(false)}>Cancel</Button>
              <Button className="admin-small-button ml-10" onClick={submitFinalPaymentSchedule}>Save</Button>
            </div>
          </Col>
        </Row>
      </Modal>
    )
  }

  const renderDueDateOption = () => {
    return (
      <>
        <Row align="middle">
          <Col xs={24}>
            <Select
              style={{ width: '100%' }}
              placeholder={"Select an option..."}
              size='large'
              value={selectedDueDateOption}
              onChange={(value) => onDueDateOptionChange(value)}
            >
              <Select.Option value="ISSUE_DATE">On issue date</Select.Option>
              <Select.Option value="EVENT_DATE">On event date</Select.Option>
              <Select.Option value="CUSTOM">Custom</Select.Option>
            </Select>
          </Col>
        </Row>
        <Row align="middle">
          <Col xs={24}>
            { selectedDueDateOption == "CUSTOM" &&  (
              <InvoicePaymentTimePicker timeObject={selectedDueDateCalculation} setTimeObject={setSelectedDueDateCalculation}/>
            )}
          </Col>
        </Row>
      </>
    )
  }

  const renderContent = () => {
    const hasTax = getTotalTaxes(lineItems) > 0
    if (hasPayments) {
      return (
        <FloatingContainer className="ph-20 mb-20 bg-gray" verticalPadding={20} maxWidth={850}>
          <div className="shadow-card-square mb-20 p-20 text-center">
            <div className="fw-700">EDIT INVOICE</div>
            <div className="mt-5 c-text-gray bg-gray p-15">This invoice can no longer be edited since payments have been recorded.</div>
          </div>
        </FloatingContainer>
      )
    }

    const paddingClass = screens.md ? "p-30" : "p-20"
    return (
      <>
        <FloatingContainer className="ph-20 mb-20 bg-gray" verticalPadding={20} maxWidth={850}>
          <div className="shadow-card-square mb-20 p-20 text-center">
            <div className="fw-700">EDIT INVOICE</div>
            <div className="mt-5 c-text-gray bg-gray p-15">Make any necessary adjustments to the invoice below. <br/> Once you are finished, you can move on to the next step.</div>
          </div>
          <div className={`shadow-card-square mt-15`}>
            <div className={`${paddingClass}`}>
              <div className="fs-20 fw-700 mb-10">Details</div>
              <Row className="" gutter={[30,30]}>
                <Col xs={24} md={12}>
                  <div className="fw-700 mb-5 c-text-gray fs-12">INVOICE ID</div>
                  <Input maxLength={20} size="large" style={{ width: 250 }} placeholder={"Enter a number or ID..."} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}/>
                </Col>
                <Col xs={24} md={12}>
                  <div className="fw-700 mb-5 c-text-gray fs-12">ISSUE DATE</div>
                  <DatePicker size="large" format={getDatePickerFormat(accountSettings)} value={issueDate} onChange={(date) => confirmIssueDate(date)}/>
                </Col>
                <Col xs={24} md={12}>
                  <div className="fw-700 mb-5 c-text-gray fs-12">BILL TO</div>
                  { renderBillToUser() }
                </Col>
              </Row>
            </div>
          </div>

          <div className={`shadow-card-square ${paddingClass} mt-15`}>
            <div className="fs-20 fw-700 mb-10">Items</div>
            { renderSummaryHeader() }
            { lineItems.length == 0 ? (
              <div className="border ph-15 pv-30 text-center mt-15">
               <div>No line items have been added yet.</div>
                <div className="blue-link" onClick={() => setAddItemModalVisible(true)}>Add item</div>
              </div>
            ) : (
              <div>
                { lineItems.map((x,i) => (renderCommonLineItem(x, i))) }
                <Row align="middle" className="pv-10 ph-15">
                  <Col xs={24}>
                    <div className="blue-link" onClick={() => setAddItemModalVisible(true)}>+ Add item</div>
                  </Col>
                </Row> 
                { hasTax && (
                  <>
                    <Row align="middle" className="pb-20 ph-15">
                      <Col xs={17} md={20}>
                        <div className="fw-700 text-right">Subtotal:</div>
                      </Col>
                      <Col xs={7} md={4}>
                        <div className="text-right">{formatCurrencyString(getSubTotal(lineItems), currency.code)}</div>
                      </Col>
                    </Row>
                    <Row align="middle" className="pb-20 ph-15">
                      <Col xs={17} md={20}>
                        <div className="fw-700 text-right">Tax:</div>
                      </Col>
                      <Col xs={7} md={4}>
                        <div className="text-right">{formatCurrencyString(getTotalTaxes(lineItems), currency.code)}</div>
                      </Col>
                    </Row>
                  </>
                )} 
                <Row align="middle" className="pb-20 ph-15">
                  <Col xs={17} md={20}>
                    <div className="fw-700 text-right">Amount Due:</div>
                  </Col>
                  <Col xs={7} md={4}>
                    <div className="fw-700 text-right">{formatCurrencyString(getAmountDue(lineItems), currency.code)}</div>
                  </Col>
                </Row> 
              </div>
            )}

            <Row className="mt-30" gutter={[20,20]}>
              <Col xs={24} md={24}>
                <div className="fs-14 fw-700 mb-5">Notes</div>
                <Input.TextArea rows={4} placeholder={"Add optional notes, terms, etc."} value={notes} onChange={(e) => setNotes(e.target.value)}/>
              </Col>
            </Row>
          </div>

          <div className={`shadow-card-square ${paddingClass} mt-15`}>
            <div className="fs-20 fw-700">Tips</div>
            <div className="c-text-gray mt-5 mb-15">Give your clients an option to leave you a tip.</div>
            <Switch checked={isTipsActive} onChange={(value) => setTipsActive(value)} /> <span className="ml-10 fw-600">{isTipsActive ? "On" : "Off"}</span>

            <div className="fs-20 fw-700 mt-30 pt-30 t-border">Payment Reminders</div>
            <div className="c-text-gray mt-5 mb-15">Send automatic reminders to your clients when each payment is due.</div>
            <Switch checked={isPaymentRemindersActive} onChange={(value) => onPaymentReminderToggle(value)} /> <span className="ml-10 fw-600">{isPaymentRemindersActive ? "On" : "Off"}</span>
            { isPaymentRemindersActive && renderPaymentReminders() }
          </div>

          <div className={`shadow-card-square ${paddingClass} mt-15`}>
            <Row className="" gutter={[20,15]}>
              <Col xs={24} md={24}>
                <div className="fs-20 fw-700 mb-5">Payment Schedule</div>
                <div className="mb-5 c-text-gray">Use a payment schedule to collect deposits/retainers or setup installments.</div>
              </Col>
              <Col xs={24} md={24}>
                <Switch checked={isPaymentScheduleActive} onChange={(value) => onPaymentScheduleToggle(value)} /> <span className="ml-10 fw-600">{isPaymentScheduleActive ? "On" : "Off"}</span>
              </Col>
              { isPaymentScheduleActive && (
                <Col xs={24} md={24}>
                  { renderPaymentSchedule() }
                </Col>
              )}
            </Row>

            { !isPaymentScheduleActive && (
              <>
                <div className="fs-20 fw-700 mt-30 pt-30 t-border">Due Date</div>
                <div className="c-text-gray mt-5 mb-15">The date the final payment is due.</div>
                <Row>
                  <Col xs={24} sm={12}>
                    { renderDueDateOption() }
                  </Col>
                </Row>
              </>
            )}
          </div>
        </FloatingContainer>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className="event-contract--container">
        <LoadingSpinner/>
      </div>
    )
  }

  return (
    <div className="event-contract--container">
      { renderHeader()}
      <div className="event-contract--body">
        <div className="event-contract--content">
          { renderContent() }
        </div>
      </div>
      { renderPayNowErrorModal() }
      { renderAddItemModal() }
      { renderBillToModal() }
      { renderDateValidModal() }
      { renderAmountsValidModal() }
      { renderZeroAmountModal() }
      { renderPaymentReminderModal() }
      { renderPaymentScheduleModal() }
      { renderFinalPaymentScheduleModal() }
    </div>
  );
}

export default EventInvoicePage;
